import { useCallback } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Configurazione predefinita per i toast
const defaultToastOptions: ToastOptions = {
  position: 'bottom-right',
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'colored',
};

// Tipi per i diversi tipi di toast
type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Hook personalizzato per gestire i toast in modo consistente
 */
export function useToast() {
  const { t } = useTranslation();

  const showToast = useCallback((
    type: ToastType,
    message: string,
    customOptions?: Partial<ToastOptions>
  ) => {
    const options = { ...defaultToastOptions, ...customOptions };
    
    switch (type) {
      case 'success':
        toast.success(message, options);
        break;
      case 'error':
        toast.error(message, options);
        break;
      case 'warning':
        toast.warning(message, options);
        break;
      case 'info':
        toast.info(message, options);
        break;
    }
  }, []);

  // Metodi specifici per ogni tipo di toast
  const success = useCallback((message: string, options?: Partial<ToastOptions>) => {
    showToast('success', message, options);
  }, [showToast]);

  const error = useCallback((message: string, options?: Partial<ToastOptions>) => {
    showToast('error', message, options);
  }, [showToast]);

  const warning = useCallback((message: string, options?: Partial<ToastOptions>) => {
    showToast('warning', message, options);
  }, [showToast]);

  const info = useCallback((message: string, options?: Partial<ToastOptions>) => {
    showToast('info', message, options);
  }, [showToast]);

  // Toast specifici per operazioni comuni
  const markerCreated = useCallback(() => {
    success(t('toast:save_pin_success'));
  }, [success, t]);

  const markerUpdated = useCallback(() => {
    success(t('toast:update_pin_success'));
  }, [success, t]);

  const markerDeleted = useCallback(() => {
    success(t('toast:delete_pin_success'));
  }, [success, t]);

  const profileUpdated = useCallback(() => {
    success(t('toast:profile_updated'));
  }, [success, t]);

  const creditsUpdated = useCallback(() => {
    success(t('toast:credits_updated'));
  }, [success, t]);

  // Toast per autenticazione e utenti
  const loginError = useCallback(() => {
    error(t('toast:login_error'));
  }, [error, t]);

  const registerError = useCallback(() => {
    error(t('toast:register_error'));
  }, [error, t]);

  const accountNotVerified = useCallback(() => {
    info(t('toast:account_not_verified'));
  }, [info, t]);

  const sendActivationLinkError = useCallback(() => {
    error(t('toast:send_activation_link_error'));
  }, [error, t]);

  const googleError = useCallback(() => {
    error(t('toast:google_error'));
  }, [error, t]);

  // Toast per password
  const resetPasswordSuccess = useCallback(() => {
    success(t('toast:reset_password_success'));
  }, [success, t]);

  const resetPasswordError = useCallback(() => {
    error(t('toast:reset_password_error'));
  }, [error, t]);

  const resetPasswordNotFound = useCallback(() => {
    error(t('toast:reset_password_not_found'));
  }, [error, t]);

  const setNewPasswordError = useCallback(() => {
    error(t('toast:set_new_password_error'));
  }, [error, t]);

  // Toast per foto profilo
  const photoDeletedSuccess = useCallback(() => {
    success(t('toast:photo_deleted_success'));
  }, [success, t]);

  const photoSuccess = useCallback(() => {
    success(t('toast:photo_success'));
  }, [success, t]);

  const photoError = useCallback(() => {
    error(t('toast:photo_error'));
  }, [error, t]);

  const photoDeletedError = useCallback(() => {
    error(t('toast:photo_deleted_error'));
  }, [error, t]);

  // Toast per impostazioni
  const settingsSuccess = useCallback(() => {
    success(t('toast:settings_success'));
  }, [success, t]);

  const settingsError = useCallback(() => {
    error(t('toast:settings_error'));
  }, [error, t]);

  // Toast per consultazioni
  const consultancySuccess = useCallback(() => {
    success(t('toast:consultancy_success'));
  }, [success, t]);

  const freeConsultancySuccess = useCallback(() => {
    success(t('toast:free_consultancy_success'));
  }, [success, t]);

  const consultancyError = useCallback(() => {
    error(t('toast:consultancy_error'));
  }, [error, t]);

  const consultancyClosedSuccess = useCallback(() => {
    success(t('toast:consultancy_closed_success'));
  }, [success, t]);

  const consultancyClosedError = useCallback(() => {
    error(t('toast:consultancy_closed_error'));
  }, [error, t]);

  const consultancyClosedErrorCode = useCallback(() => {
    error(t('toast:consultancy_closed_error_code'));
  }, [error, t]);

  // Toast per utenti
  const deleteUserSuccess = useCallback(() => {
    success(t('toast:delete_user_success'));
  }, [success, t]);

  const deleteUserError = useCallback(() => {
    error(t('toast:delete_user_error'));
  }, [error, t]);

  // Toast per AI Chat
  const aiChatError = useCallback(() => {
    error(t('toast:ai_chat_error'));
  }, [error, t]);

  // Toast per condivisione
  const shareModalSuccess = useCallback(() => {
    success(t('toast:share_modal_success'));
  }, [success, t]);

  const referralModalSuccess = useCallback(() => {
    success(t('toast:referral_modal_success'));
  }, [success, t]);

  // Toast per errori generici
  const genericError = useCallback((operation: string) => {
    error(t('toast:generic_error'));
  }, [error, t]);

  const networkError = useCallback(() => {
    error(t('toast:network_error'));
  }, [error, t]);

  const validationError = useCallback((message: string) => {
    warning(message);
  }, [warning]);

  const welcomeError = useCallback(() => {
    error(t('toast:welcome_error'));
  }, [error, t]);

  const pinnersFetchError = useCallback(() => {
    error(t('toast:pinners_fetch_error'));
  }, [error, t]);

  const pinnersError = useCallback(() => {
    error(t('toast:pinners_error'));
  }, [error, t]);

  return {
    // Metodi generici
    showToast,
    success,
    error,
    warning,
    info,
    
    // Toast specifici per operazioni
    markerCreated,
    markerUpdated,
    markerDeleted,
    profileUpdated,
    creditsUpdated,
    
    // Toast per autenticazione e utenti
    loginError,
    registerError,
    accountNotVerified,
    sendActivationLinkError,
    googleError,
    
    // Toast per password
    resetPasswordSuccess,
    resetPasswordError,
    resetPasswordNotFound,
    setNewPasswordError,
    
    // Toast per foto profilo
    photoDeletedSuccess,
    photoSuccess,
    photoError,
    photoDeletedError,
    
    // Toast per impostazioni
    settingsSuccess,
    settingsError,
    
    // Toast per consultazioni
    consultancySuccess,
    freeConsultancySuccess,
    consultancyError,
    consultancyClosedSuccess,
    consultancyClosedError,
    consultancyClosedErrorCode,
    
    // Toast per utenti
    deleteUserSuccess,
    deleteUserError,
    
    // Toast per AI Chat
    aiChatError,
    
    // Toast per condivisione
    shareModalSuccess,
    referralModalSuccess,
    
    // Toast per errori comuni
    genericError,
    networkError,
    validationError,
    welcomeError,
    pinnersFetchError,
    pinnersError,
    
    // Metodi di utilità
    dismiss: toast.dismiss,
    isActive: toast.isActive,
  };
}
